export const APP = {
  NAME: "Stack Digital Studio", // used in UI, easy to swap
  SHORT_NAME: "Stack Digital",
  SUPPORT_EMAIL: "hello@stackdigital.eu",
};

// Future model wiring — placeholders only, NOT used in Wave 1
export const MODELS = {
  COPY: "MOCK", // later: text model id
  ANALYST: "MOCK", // later: analysis model id
  IMAGE_PREVIEW: "MOCK", // later: fast image model id
  IMAGE_FINAL: "MOCK", // later: quality image model id
};

export const CREDITS = {
  SIGNUP_GRANT: 20,
};

export const RSA = {
  HEADLINE_COUNT: 15,
  HEADLINE_MAX_CHARS: 30,
  DESCRIPTION_COUNT: 4,
  DESCRIPTION_MAX_CHARS: 90,
  PATH_MAX_CHARS: 15, // 2 path fields
  MIN_KEYWORD_COVERAGE: 0.6, // share of target keywords reflected in assets
  DIVERSITY_MIN_SCORE: 0.5, // below this, flag headline set as repetitive
  MAX_REGEN_ATTEMPTS: 3,
};

export const ANGLES = [
  "price_promo",
  "social_proof",
  "urgency",
  "benefit_led",
  "problem_agitation",
  "brand",
];

export const OBJECTIVES = [
  "awareness",
  "traffic",
  "leads",
  "sales",
  "app_installs",
];

export const UTM = {
  SOURCE_BY_CHANNEL: {
    google_ads: "google",
    social: "{platform}",
    display: "programmatic",
  },
  MEDIUM_BY_CHANNEL: {
    google_ads: "cpc",
    social: "paid_social",
    display: "display",
  },
  // campaign: {brand_slug}_{objective}_{yyyymm} ; content: {asset_id}
  CAMPAIGN_PATTERN: "{brand}_{objective}_{yyyymm}",
  CONTENT_PATTERN: "{asset_id}",
};

export const LIMITS = {
  MAX_BRANDS_PER_WORKSPACE: 20,
  MAX_REFERENCE_IMAGES_PER_BRAND: 30,
  MAX_UPLOAD_MB: 10,
};

// ---------------------------------------------------------------------------
// Industries — brand-level context that steers generation (tone flavor) and,
// for regulated verticals, forces a compliance line into every RSA.
// ---------------------------------------------------------------------------

export const INDUSTRIES = [
  { value: "ecommerce_retail", label: "E-commerce & Retail" },
  { value: "saas_software", label: "SaaS & Software" },
  { value: "finance_insurance", label: "Finance & Insurance", regulated: true },
  { value: "health_wellness", label: "Health & Wellness", regulated: true },
  { value: "gambling", label: "Gambling & Betting", regulated: true },
  { value: "gaming_entertainment", label: "Gaming & Entertainment" },
  { value: "travel_hospitality", label: "Travel & Hospitality" },
  { value: "education", label: "Education" },
  { value: "real_estate", label: "Real Estate" },
  { value: "automotive", label: "Automotive" },
  { value: "food_beverage", label: "Food & Beverage" },
  { value: "fashion_beauty", label: "Fashion & Beauty" },
  { value: "home_services", label: "Home & Trade Services" },
  { value: "b2b_services", label: "B2B & Professional Services" },
  { value: "legal", label: "Legal Services", regulated: true },
  { value: "other", label: "Other" },
];

export function industryOption(value) {
  return INDUSTRIES.find((i) => i.value === value);
}

// ---------------------------------------------------------------------------
// Languages — the language every generated & exported asset is written in.
// Only languages the copy engine fully supports (templates + translated
// compliance lines); more arrive with the real model providers.
// ---------------------------------------------------------------------------

export const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "pt", label: "Português" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
];

export const DEFAULT_LANGUAGE = "en";

// ---------------------------------------------------------------------------
// Wave 2 — HTML5 Display Studio (SPEC-WAVE2 §1)
//
// Spec caps verified against official docs on 2026-07-13:
// CM360 itself accepts HTML5 zips up to 9.5 MB / 100 files
// (support.google.com/campaignmanager/answer/3145300), but programmatic
// exchanges and IAB display guidance budget far tighter — MAX_ZIP_KB stays
// at the exchange-safe 200 KB per the spec. clickTag convention confirmed
// (complete HTML document, >=1 click tag, iframe-loadable, no local storage).
// ---------------------------------------------------------------------------

export const DISPLAY = {
  // `channel` decides the export format: "display" → HTML5 zip (clickTag, for
  // programmatic/CM360); "social" → animated GIF (paid social feeds).
  SIZES: [
    { id: "300x250", w: 300, h: 250, label: "Medium Rectangle", core: true, channel: "display" },
    { id: "336x280", w: 336, h: 280, label: "Large Rectangle", core: false, channel: "display" },
    { id: "728x90", w: 728, h: 90, label: "Leaderboard", core: true, channel: "display" },
    { id: "160x600", w: 160, h: 600, label: "Wide Skyscraper", core: true, channel: "display" },
    { id: "300x600", w: 300, h: 600, label: "Half Page", core: true, channel: "display" },
    { id: "320x50", w: 320, h: 50, label: "Mobile Banner", core: true, channel: "display" },
    { id: "320x100", w: 320, h: 100, label: "Large Mobile", core: false, channel: "display" },
    { id: "970x250", w: 970, h: 250, label: "Billboard", core: false, channel: "display" },
    // Paid social — exported as GIF, edited in the same builder.
    { id: "1080x1080", w: 1080, h: 1080, label: "Square (Feed)", core: false, channel: "social" },
    { id: "1080x1920", w: 1080, h: 1920, label: "Story / Reel", core: false, channel: "social" },
    { id: "1200x630", w: 1200, h: 630, label: "Landscape (Link)", core: false, channel: "social" },
  ],
  CORE_SET_DEFAULT: ["300x250", "728x90", "160x600", "300x600", "320x50"],

  PROFILE: {
    // programmatic clickTag — the only profile this wave
    id: "programmatic_clicktag",
    MAX_ZIP_KB: 1000, // per-size zip, hard fail (above 250 KB)
    MAX_INITIAL_KB_WARN: 400, // soft warning threshold — up to 150 KB is fine
    MAX_ANIMATION_S: 30,
    MAX_LOOPS: 3, // then hold end frame
    EXTERNAL_REQUESTS_ALLOWED: false,
  },
  // seam: additional profiles (e.g. Google Ads Exit API) plug in later as siblings

  ANIMATION_PRESETS: ["static", "fade_sequence", "slide_build", "cta_pulse_finale"],
  FRAME_HOLD_MIN_S: 2,

  CREDITS: {
    BANNER_SET: 3, // charged once at asset creation, idempotent like RSA
    IMAGE_PREVIEW_BATCH: 2, // 4 mock previews per batch
  },
  IMG_MAX_KB_WARN: 150,
  GENERATED_MASTER_PX: 1200, // generated images: 1200x1200, runtime crops per slot

  // Paid-social GIF export profile (the "social"-channel sizes). Frames are
  // rasterized server-side; GIFs run heavier than HTML5, so the budget is
  // looser than the programmatic zip cap.
  SOCIAL: {
    MAX_GIF_KB: 8000, // per-size, hard fail
    WARN_GIF_KB: 5000, // soft warning
    FPS: 17, // reduced from 24 (30% cut) - confirmed via testing to meaningfully reduce GIF size at an acceptable smoothness cost; identical held frames are still coalesced by the encoder regardless of rate
    MAX_SECONDS: 15,
  },
};

export function displaySize(id) {
  return DISPLAY.SIZES.find((s) => s.id === id);
}

/** The channel a size belongs to (defaults to display for unknown ids). */
export function displayChannel(id) {
  return displaySize(id)?.channel ?? "display";
}

/** How a given size is exported: social sizes → animated GIF, else HTML5 zip. */
export function exportFormatFor(id) {
  return displayChannel(id) === "social" ? "gif" : "html5";
}

// ---------------------------------------------------------------------------
// Role gating (admin/editor/viewer, from Auth0's stack_user_metadata.role,
// already threaded into config.userRole throughout the app - see App.js).
// Frontend-only by design for now: admin ⊇ editor for everything except
// export, which every role can do regardless (a viewer can only ever
// export an asset an editor/admin already approved, since approve itself
// is gated - draft assets still can't be exported at all, unrelated to role).
// ---------------------------------------------------------------------------

export function isAdmin(role) {
  return role === "admin";
}

/** True for editor or admin - the "can actually change things" tier. */
export function canEdit(role) {
  return role === "admin" || role === "editor";
}

// ---------------------------------------------------------------------------
// Generate-flow question config (SPEC §6.2): the wizard renders from this
// typed data. Adding, reordering, or rewording questions touches ONLY this
// file — never component code.
// ---------------------------------------------------------------------------

/** Display source mode (Wave 2 Stage B): platform-composed vs. bring-your-own-image. */
export const DISPLAY_SOURCE_PLATFORM = "platform";
export const DISPLAY_SOURCE_BYO = "byo";
/** Bring-your-own uses the custom-image frame (your creative fills every size, no scrim). */
export const BYO_TEMPLATE_ID = "custom-image";

export const DISPLAY_SOURCE_OPTIONS = [
  {
    value: DISPLAY_SOURCE_PLATFORM,
    label: "Platform-composed",
    description:
      "We lay out your logo, copy, and image into each size. Best for multi-size sets and crisp, reflowing text.",
  },
  {
    value: DISPLAY_SOURCE_BYO,
    label: "Bring your own image",
    description:
      "Upload a finished creative — text can already be in it. We wrap it for ad servers (clickTag, border, compliance).",
  },
];

// The builder makes one thing — HTML5 display banners (some sizes export as
// GIF). The wizard renders straight from this list; no asset-type branching.
export const GENERATE_WIZARD_STEPS = [
  {
    key: "offer",
    kind: "offer",
    title: "Which offer?",
    subtitle: "Pick one of this brand's offers, or add one now.",
  },
  {
    key: "landing_url",
    kind: "url",
    title: "Landing page",
    subtitle: "Where the ad clicks through to. Prefilled from the offer.",
  },
  {
    key: "display_source",
    kind: "display_source",
    title: "How are you building these?",
    subtitle: "Let the platform compose the banners, or bring your own finished image.",
  },
  {
    key: "display_language",
    kind: "cards",
    title: "Banner language",
    subtitle: "Copy defaults and compliance lines are written in this language.",
    options: LANGUAGES.map((l) => ({
      value: l.value,
      label: l.label,
      description: `Generated banner copy in ${l.label}.`,
    })),
  },
  {
    key: "display_sizes",
    kind: "display_sizes",
    title: "Banner sizes",
    subtitle: "The core IAB set is preselected. Paid-social formats export as GIF.",
  },
  {
    key: "display_template",
    kind: "display_template",
    title: "Template",
    subtitle: "The layout system for every size. Sizes a template doesn't support are dropped.",
  },
];

/** HTML5 template step keeps a curated shortlist — two layouts plus custom. */
export const DISPLAY_TEMPLATE_CHOICES = ["logo-lockup", "full-bleed", "custom-image"];

/**
 * Data the wizard's template step needs to render cards and validate a
 * selection against chosen sizes — id/name/description/supportedSizes only.
 * The original's SEED_TEMPLATES also carries the actual per-size layout
 * (positions, font sizes, etc.) used by the rendering engine — that's
 * Composer-build territory, deliberately not ported here yet. Two other
 * templates exist in the original (product-right, split-band) but aren't
 * in DISPLAY_TEMPLATE_CHOICES, so the wizard never shows them either.
 */
export const WIZARD_TEMPLATES = [
  {
    id: "logo-lockup",
    name: "Logo Lockup",
    version: 1,
    description: "Logo, headline, and CTA on the brand color — the safe default for every size.",
    supportedSizes: DISPLAY.SIZES.map((s) => s.id),
  },
  {
    id: "full-bleed",
    name: "Full Bleed",
    version: 1,
    description: "Image background with a gradient scrim and overlaid copy.",
    supportedSizes: ["300x250", "336x280", "160x600", "300x600"],
  },
  {
    id: "custom-image",
    name: "Custom Image",
    version: 1,
    description:
      "Your uploaded creative fills every size — no scrim. Text defaults to baked; a version per size is optional.",
    supportedSizes: DISPLAY.SIZES.map((s) => s.id),
  },
];